import { useLazyQuery } from '@apollo/client';
import { useEffect, useState, useContext, useRef } from 'react';
import { Button, Card, Col, FormGroup, Label, Row } from 'reactstrap';
import InputField from '../../../components/layout/InputField';
import { CompanySource, contactStatus } from '../../../utils/constant';
import { FETCH_All_DropDown_COMPANIES, GET_COMPANY_ADDRESS } from '../../company/gql';
import CreateAddressModal from './CreateAddressModal';
import CreateCompanyModal from './CreateCompanyModal';
import { AppContext } from '../../../context';
import '../../company/company.scss';
import { Controller } from 'react-hook-form';
import AsyncCompanyDropdown from '../../../components/AsyncCompanySelect';
import Select from 'react-select';
import { customSelectDropDown } from '../../../components/styled/customSelect';
import ChangeContactCompanyModal from './ChangeContactCompanyModal';

const BasicContactInfo = ({
  register,
  errors,
  setValue,
  _company,
  location,
  control,
  makeCompanyReadOnly = false,
  isPreferredContact,
  ...props
}: any) => {
  const { theme } = useContext(AppContext);
  const [getCompanies, { data }] = useLazyQuery(FETCH_All_DropDown_COMPANIES);
  const [getCompanyAddress, { data: companyAddressData }] = useLazyQuery(GET_COMPANY_ADDRESS);
  const lastFetchedCompanyIdRef = useRef<string | null>(null);
  const [addressList, setAddressList] = useState<Array<any>>([]);
  const [companyId, setCompanyId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [modal, setModal] = useState(false);
  const toggle = () => setModal(!modal);
  const [companyModal, setCompanyModal] = useState(false);
  const [changeCompanyPop, setChangeCompanyPop] = useState(false);
  const [isPreferred, setIsPreferred] = useState(isPreferredContact);
  const companyToggle = () => setCompanyModal(!companyModal);

  const buildAddressOptions = (addresses: any[]) => {
    if (!addresses?.length) return [];
    const options = addresses.map((v: any) => {
      let label = ['US', null, undefined, ''].includes(v?.country) ? `${v?.streetAddress}, ${v?.city}, ${v?.state}, ${v?.zipCode}, ${v?.country}` : `${v?.streetAddress}, ${v?.city}, ${v?.zipCode}, ${v?.country}`;
      if (v?.division) {
        label = `${v.division}, ${label}`;
      }
      return { label, value: v.id };
    });
    options.sort((a, b) => {
      const aHasDivision = addresses.find((addr) => addr.id === a.value)?.division;
      const bHasDivision = addresses.find((addr) => addr.id === b.value)?.division;
      if (aHasDivision && !bHasDivision) return -1;
      if (!aHasDivision && bHasDivision) return 1;
      if (a.label < b.label) return -1;
      if (a.label > b.label) return 1;
      return 0;
    });
    return options;
  };

  const changeCompany = (value: any) => {
    setCompanyId(value);
    let companyAddress = data?.companiesForDropDown?.companies?.find(
      (item: any) => item?.id === value
    );
    if (companyAddress && companyAddress?.address) {
      setAddressList(buildAddressOptions(companyAddress.address));
    }
  };

  useEffect(() => {
    if (props?.getValues()?.companyId) {
      let company = props?.getValues()?.companyId;

      setCompanyId(company?.value);
      setCompanyName(company?.label);
      changeCompany(company?.value);
      setIsPreferred(isPreferredContact);
    }
    // eslint-disable-next-line
  }, [props?.getValues()?.companyId]);

  useEffect(() => {
    CompanySource.sort(function (a, b) {
      if (a.label < b.label) {
        return -1;
      }
      if (a.label > b.label) {
        return 1;
      }
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [CompanySource]);

  const fetchCompanies = () => {
    getCompanies({
      variables: {
        limit: 20,
        page: 1,
        searchText: companyName,
      },
    });
  };

  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (
      companyId &&
      data?.companiesForDropDown?.companies?.length &&
      companyName
    ) {
      let company = data?.companiesForDropDown?.companies?.find(
        (item) => item.id === companyId
      );

      if (company) {
        changeCompany(company?.id);
      } else {
        getCompanies({
          variables: {
            limit: 20,
            page: 1,
            searchText: companyName,
          },
        });
      }
    }
    // eslint-disable-next-line
  }, [companyId, companyName, data]);

  // When selected company is not in dropdown cache (e.g. from owner-based Company dropdown), fetch addresses by companyId
  useEffect(() => {
    if (!companyId) return;
    const companyInCache = data?.companiesForDropDown?.companies?.find(
      (item: any) => item?.id === companyId
    );
    if (companyInCache?.address) return;
    lastFetchedCompanyIdRef.current = companyId;
    getCompanyAddress({
      variables: { id: companyId, page: 1, limit: 100 },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, data?.companiesForDropDown?.companies]);

  useEffect(() => {
    const addresses = companyAddressData?.getCompanyAddress?.address;
    if (!addresses?.length || lastFetchedCompanyIdRef.current !== companyId) return;
    setAddressList(buildAddressOptions(addresses));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyAddressData, companyId]);

  const preSelectAddress = (location) => {
    let label = ['US', null, undefined, ''].includes(location?.country) ? `${location?.streetAddress}, ${location?.city}, ${location?.state}, ${location?.zipCode}, ${location?.country}` : `${location?.streetAddress}, ${location?.city}, ${location?.zipCode}, ${location?.country}`;
    if (location?.division) {
      label = `${location.division}, ${label}`;
    }
    setValue('locationId', {
      label,
      value: location?.id,
    });
  };

  const preSelectCompany = (company) => {
    setValue('companyId', {
      label: company?.name,
      value: company?.id,
    });
    changeCompany(company?.id);
    setCompanyName(company?.name);
    setAddressList([]);
    setValue('locationId', null);
  };

  return (
    <div>
      <Card className="border-0 mb-2">
        <div className="user-basic-form">
          <Row>
            <Col lg="4" sm="12" xs="12">
              <InputField
                inputtype="text"
                isRequired={true}
                label="First Name"
                inputid="firstName"
                placeholder="First Name"
                {...register('firstName', {
                  required: {
                    value: true,
                    message: 'First name is required!',
                  },
                })}
                errMsg={errors?.firstName?.message as any}
              />
            </Col>

            <Col lg="4" sm="12" xs="12">
              <InputField
                inputtype="text"
                isRequired={true}
                label="Last Name"
                inputid="lastName"
                placeholder="Last Name"
                {...register('lastName', {
                  required: {
                    value: true,
                    message: 'Last name is required!',
                  },
                })}
                errMsg={errors?.lastName?.message as any}
              />
            </Col>

            <Col lg="4" sm="12" xs="12">
              <InputField
                inputtype="text"
                isRequired={true}
                label="Job Title"
                inputid="jobTitle"
                placeholder="job Title"
                {...register('jobTitle', {
                  required: {
                    value: true,
                    message: 'Job title is required!',
                  },
                })}
                errMsg={errors?.jobTitle?.message as any}
              />
            </Col>
            {props?.isEditable && (
              <Col lg="4" sm="12" xs="12">
                <InputField
                  label="Status"
                  inputtype="select"
                  selectItems={contactStatus}
                  inputid="status"
                  placeholder="Status"
                  {...register('status', { required: false })}
                />
              </Col>
            )}
            <Col lg="4" sm="12" xs="12">
              {props?.isEditable ? (
                <InputField
                  label="Source"
                  isRequired={true}
                  readOnly
                  inputtype="text"
                  // selectItems={CompanySource}
                  inputid="source"
                  placeholder="Source"
                  {...register('source', {
                    required: { value: true, message: 'Source is required!' },
                  })}
                  errMsg={errors?.source?.message as any}
                />
              ) : (
                <InputField
                  label="Source"
                  isRequired={true}
                  inputtype="select"
                  selectItems={CompanySource}
                  inputid="source"
                  placeholder="Source"
                  {...register('source', {
                    required: { value: true, message: 'Source is required!' },
                  })}
                  errMsg={errors?.source?.message as any}
                />
              )}
            </Col>

            <Col lg="4" sm="12" xs="12">
              <Label>
                Company <span className="text-danger">*</span>
              </Label>
              <Controller
                name="companyId"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: 'Please select a company!',
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <AsyncCompanyDropdown
                    width={'100%'}
                    ownershareeFlag={true}
                    placeholder="Filter By Company name"
                    value={value}
                    isDisabled={makeCompanyReadOnly}
                    onChange={(val) => {
                      if (val) {
                        setCompanyName(val?.label);
                        changeCompany(val?.value);
                        setValue('locationId', null);
                      }
                      onChange(val);
                    }}
                    ownerIdFlag={true}
                    styles={{ ...customSelectDropDown(theme) }}
                  />
                )}
              />

              <div className="d-flex justify-content-between mt-2">
                <small className="text-danger">
                  {errors?.companyId?.message as any}
                </small>
                {!makeCompanyReadOnly && (
                  <Button
                    type="button"
                    onClick={companyToggle}
                    className="bg-transparent border-0 text-info p-0"
                  >
                    Create New Company
                  </Button>
                )}
                {props?.isEditable && (
                  <Button
                    type="button"
                    onClick={() => setChangeCompanyPop(!changeCompanyPop)}
                    className="bg-transparent border-0 text-info p-0"
                  >
                    Change Company
                  </Button>
                )}
              </div>
            </Col>

            <Col lg="4" sm="12" xs="12" className="create-new-company">
              <FormGroup>
                <Label>Location</Label>
                <Controller
                  name="locationId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={addressList}
                      placeholder="Location"
                      isSearchable={true}
                      styles={{ ...customSelectDropDown(theme) }}
                    />
                  )}
                />
                <small className="text-danger pt-2">
                  {errors?.locationId?.message as any}
                </small>
              </FormGroup>
              <div className="d-flex justify-content-end mt-2">
                <Button
                  className="bg-transparent border-0 text-info p-0"
                  onClick={toggle}
                  type="button"
                  disabled={companyId ? false : true}
                  title={
                    !companyId ? 'Kindly select a company for new address!' : ''
                  }
                >
                  Create New Address
                </Button>
              </div>
            </Col>

            <Col lg="4" sm="12" xs="12">
              <FormGroup>
                <Label for="preferredContact">Preferred Contact</Label>
                <div className="d-flex items-center" style={{ marginTop: '5px' }}>
                  <Label className="radio-container">
                    Preferred Contact
                    <input
                      type="checkbox"
                      id="preferredContact"
                      {...register("preferredContact", {
                        onChange: (e) => {
                          setIsPreferred(e.target.checked);
                        }
                      })}
                      checked={isPreferred}
                    />
                    <span className="checkmark"></span>
                  </Label>
                </div>
              </FormGroup>
            </Col>
          </Row>
        </div>
      </Card>

      <CreateAddressModal
        open={modal}
        toggle={toggle}
        companyId={companyId}
        preSelect={preSelectAddress}
      />
      <CreateCompanyModal
        open={companyModal}
        toggle={companyToggle}
        preSelectCompany={preSelectCompany}
      />

      <ChangeContactCompanyModal
        toggle={() => setChangeCompanyPop(!changeCompanyPop)}
        isOpen={changeCompanyPop}
        fetchContactDetail={props?.fetchContactDetail}
        contactId={props?.match?.params?.id}
      />
    </div>
  );
};

export default BasicContactInfo;
